// DataTable.tsx
import React, { DragEvent, useEffect, useMemo, useRef, useState } from 'react';

import { Column, TableProps, TableRow } from './hook/types';
import { useTableEngine } from './hook/useTableEngine';
import { divide } from 'lodash';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import Modal from 'antd/es/modal/Modal';
import ModalFilterTable from './Modal/ModalFilterTable';

type WithRowClass<T> = {
    rowClassName?: (row: TableRow<T>, index: number) => string;
};

type DataTableProps<T> = TableProps<T> & WithRowClass<T>;

function useColumnResize(ref: React.RefObject<HTMLTableElement>, columns: Column<any>[]) {
    const [widths, setWidths] = React.useState<Record<string, number>>(
        Object.fromEntries(columns.map(c => [c.id, c.width ?? 160]))
    );

    const measureMinByHeader = React.useCallback((cid: string) => {
        const table = ref.current;
        if (!table) return 48;
        const th = table.querySelector<HTMLTableCellElement>(`th[data-cid="${cid}"]`);
        const btn = th?.querySelector<HTMLElement>('.dt-th-btn');
        if (!th || !btn) return 48;

        const prevWidth = btn.style.width;
        const prevMaxWidth = (btn.style as any).maxWidth || '';

        btn.style.width = 'max-content';
        (btn.style as any).maxWidth = 'col-resize';

        const intrinsic = Math.ceil(btn.getBoundingClientRect().width);

        btn.style.width = prevWidth;
        (btn.style as any).maxWidth = prevMaxWidth;

        return intrinsic + 8;
    }, [ref]);

    const onMouseDown = (cid: string, e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = widths[cid] ?? 160;

        const col = columns.find(c => c.id === cid);
        const min = (col?.minWidth) ?? 30;   // 👈 Cho phép nhỏ hơn
        const max = (col?.maxWidth) ?? 600;

        const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            const next = Math.min(max, Math.max(min, startWidth + dx));
            setWidths(prev => ({ ...prev, [cid]: next }));
            // console.log(next)
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const onDoubleClick = (cid: string) => {
        const col = columns.find(c => c.id === cid);
        const confMin = (col?.minWidth) ?? 0;
        const w = Math.max(confMin, measureMinByHeader(cid));
        setWidths(prev => ({ ...prev, [cid]: w }));
    };

    useEffect(() => {
        setWidths(w => {
            const next = { ...w };
            columns.forEach(c => {
                if (next[c.id] == null) next[c.id] = c.width ?? 160;
            });
            return next;
        });
    }, [columns]);

    return { widths, onMouseDown, onDoubleClick };
}

function reorder<T>(arr: T[], from: number, to: number) {
    const copy = arr.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
}

export function DataTable<T>(props: DataTableProps<T>) {
    const { ischeckBox, columns, className, stickyHeader = true, filterColumn = [], rowClassName, openModalFilter } = props;

    const { state, rows, setGlobalFilter, setColumnFilter, toggleSort,
        toggleSelectAllPage, toggleSelectRow, startEdit, stopEdit } = useTableEngine<T>(props);

    const allColumns = columns;

    const tableRef = useRef<HTMLTableElement>(null);
    const { widths, onMouseDown, onDoubleClick } = useColumnResize(tableRef, allColumns);

    const [colOrder, setColOrder] = useState<string[]>(() => allColumns.map(c => c.id));
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [dropPreview, setDropPreview] = useState<{ targetId: string; side: 'left' | 'right' } | null>(null);

    const isAllSelected = rows.length > 0 && rows.every(r => state.selected.has(r.id));

    const orderedColumns = useMemo(() => {
        const map = new Map(colOrder.map((id, idx) => [id, idx]));
        return allColumns.slice().sort((a, b) => (map.get(a.id)! - map.get(b.id)!));
    }, [allColumns, colOrder]);

    const getDropSide = (thEl: HTMLTableCellElement, clientX: number): 'left' | 'right' => {
        const rect = thEl.getBoundingClientRect();
        return clientX < rect.left + rect.width / 2 ? 'left' : 'right';
    };

    const handleDragStart = (cid: string) => (e: DragEvent) => {
        setDraggingId(cid);
        e.dataTransfer.setData('text/x-col-id', cid);
        const th = tableRef.current?.querySelector<HTMLTableCellElement>(`th[data-cid="${cid}"]`);
        if (th && e.dataTransfer.setDragImage) {
            e.dataTransfer.setDragImage(th, th.clientWidth / 2, th.clientHeight / 2);
        }
    };

    const handleDragOver = (targetId: string) => (e: DragEvent) => {
        e.preventDefault();
        const th = e.currentTarget as HTMLTableCellElement;
        const side = getDropSide(th, e.clientX);
        setDropPreview({ targetId, side });
    };

    const handleDragLeave = () => setDropPreview(null);

    const handleDrop = (targetId: string) => (e: DragEvent) => {
        e.preventDefault();
        const srcId = e.dataTransfer.getData('text/x-col-id');
        if (!srcId || srcId === targetId) {
            setDraggingId(null);
            setDropPreview(null);
            return;
        }
        const side = dropPreview?.side ?? 'left';
        const current = orderedColumns.map(c => c.id);
        const from = current.indexOf(srcId);
        let to = current.indexOf(targetId);
        if (side === 'right') to = to + 1;
        const next = reorder(current, from, to > from ? to - 1 : to);
        setColOrder(next);
        setDraggingId(null);
        setDropPreview(null);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDropPreview(null);
    };

    useEffect(() => {
        const idsNow = allColumns.map(c => c.id);
        setColOrder(prev => {
            const kept = prev.filter(id => idsNow.includes(id));
            const toAdd = idsNow.filter(id => !kept.includes(id));
            return [...kept, ...toAdd];
        });
    }, [allColumns]);

    return (
        <div className={`dt-wrapper ${className ?? ''}`}>
            {/* Table */}
            <div className={`dt-table-container ${stickyHeader ? 'sticky' : ''}`}>
                <table ref={tableRef} className="dt-table">
                    <thead>
                        <tr>
                            <th className="dt-cell dt-checkbox" hidden={!ischeckBox}>
                                <input
                                    aria-label="Select all"
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={toggleSelectAllPage}
                                />
                            </th>
                            {orderedColumns.map((col, i) => {
                                const isDragging = draggingId === col.id;
                                const showLeft = dropPreview?.targetId === col.id && dropPreview?.side === 'left';
                                const showRight = dropPreview?.targetId === col.id && dropPreview?.side === 'right';
                                return (
                                    <th
                                        key={col.id}
                                        data-cid={col.id}
                                        style={{
                                            width: widths[col.id],
                                            opacity: isDragging ? 0.6 : 1,
                                            position: (showLeft || showRight) ? 'relative' : undefined,
                                            ...(showLeft ? { boxShadow: 'inset 2px 0 0 currentColor' } : {}),
                                            ...(showRight ? { boxShadow: 'inset -2px 0 0 currentColor' } : {}),
                                        }}
                                        className="dt-cell dt-th"
                                        draggable
                                        onDragStart={handleDragStart(col.id)}
                                        onDragOver={handleDragOver(col.id)}
                                        onDragLeave={handleDragLeave}
                                        onDragEnd={handleDragEnd}
                                        onDrop={handleDrop(col.id)}
                                    >
                                        <button
                                            className={`dt-th-btn ${state.sort.columnId === col.id ? 'active' : ''}`}
                                        // disabled={!col.sortable || !!draggingId}

                                        // onClick={() => console.log(col.id)}
                                        >
                                            <span className="dt-th-text text-left"
                                                onClick={() => col.sortable && !draggingId && toggleSort(col.id)}
                                            >{col.header}</span>
                                            {state.sort.columnId === col.id && (
                                                <span className="dt-sort-indicator">
                                                    {state.sort.direction === 'asc' ? '▲' : '▼'}
                                                </span>
                                            )}
                                            {filterColumn.includes(col.id) &&
                                                <div onClick={() => openModalFilter(true, col.id)} >
                                                    {/* <FilterFilled /> */}

                                                    <FilterOutlined />
                                                </div>
                                            }
                                            <span
                                                className={`dt-resizer ${orderedColumns.length == i + 1 ? 'd-none' : ''}`}
                                                onMouseDown={(e) => onMouseDown(col.id, e)}
                                                onDoubleClick={() => onDoubleClick(col.id)}
                                            />
                                        </button>

                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, rowIndex) => {
                            const extra = rowClassName ? rowClassName(r, rowIndex) : '';
                            return (
                                <tr key={r.id} className={`${state.selected.has(r.id) ? 'is-selected' : ''} ${extra}`}>
                                    <td className="dt-cell dt-checkbox" hidden={!ischeckBox}>
                                        <input
                                            type="checkbox"
                                            checked={state.selected.has(r.id)}
                                            onChange={() => toggleSelectRow(r.id)}
                                        />
                                    </td>
                                    {orderedColumns.map(col => {
                                        const raw = col.accessor ? col.accessor(r.data) : (r.data as any)[col.id];
                                        const value = (raw as any)?.props?.children ?? raw;
                                        const editing = state.editingCell?.rowId === r.id && state.editingCell?.columnId === col.id;
                                        const onChange = (next: any) => {
                                            (r.data as any)[col.id] = next;
                                        };
                                        return (
                                            <td
                                                key={col.id}
                                                className="dt-cell"
                                                style={{ width: widths[col.id] }}
                                                onDoubleClick={() => col.editable && startEdit(r.id, col.id)}
                                            >
                                                {editing && col.editable ? (
                                                    <input
                                                        className="dt-input dt-edit"
                                                        autoFocus
                                                        defaultValue={value ?? ''}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                onChange((e.target as HTMLInputElement).value);
                                                                stopEdit();
                                                            } else if (e.key === 'Escape') {
                                                                stopEdit();
                                                            }
                                                        }}
                                                        onBlur={() => stopEdit()}
                                                    />
                                                ) : (
                                                    col.renderCell
                                                        ? col.renderCell({ value, row: r.data, rowIndex, editing, onChange })
                                                        : <span>{String(value ?? '')}</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td className="dt-cell dt-empty" colSpan={orderedColumns.length + 1}>
                                    Không có dữ liệu phù hợp.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
